import * as React from 'react';
import {
    CityWithLocations,
    LocationWithImageData,
    NearbyLocation as NearbyLocationType,
} from 'lib/types';
import usePrevious from 'core/hooks/usePrevious';
import useCitiesWithLocations from 'core/hooks/useCitiesWithLocations';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import classnames from 'classnames';
import useIsMobile from 'core/hooks/useIsMobile';
import { Swiper, SwiperSlide } from 'swiper/react';
import Map from 'core/components/Map';
import LocationMarker from 'core/components/LocationMarker';
import AutoSizer from 'react-virtualized-auto-sizer';
import ClinicCard from 'designSystem/components/ClinicCard';
import { gql } from '@apollo/client';
import useLazyBookingQuery from 'booking/hooks/useLazyBookingQuery';
import useScrollControl from 'core/hooks/useScrollControl';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import { useIntl } from 'react-intl';
import * as styles from './NearbyLocations.module.scss';

export type NearbyLocationsProps = {
    location: LocationWithImageData;
};

const locationDistanceQuery = gql`
    query locationDistanceQuery($zipCode: String!) {
        nearbyLocations(zipCode: $zipCode) {
            distance
            locationId
        }
    }
`;

type LocationDistancesQuery = {
    nearbyLocations: ReadonlyArray<NearbyLocationType>;
};

function NearbyLocations({ location }: NearbyLocationsProps) {
    const [selectedLocation, setSelectedLocation] = React.useState(
        location._id,
    );
    const isMobile = useIsMobile();
    const [citySlug, setCitySlug] = React.useState(location.city?.slug);
    const [zipCode, setZipCode] = React.useState('');
    const [zipError, setZipError] = React.useState(false);
    const previousCitySlug = usePrevious(citySlug);
    const { scroll, ref } = useScrollControl('vertical');
    const intl = useIntl();

    // TODO: only needed as part of MUI fix
    const selectRef = React.useRef<HTMLDivElement>(null);

    const [order, setOrder] = React.useState<
        ReadonlyArray<NearbyLocationType> | undefined
    >(undefined);

    const citiesWithLocations = useCitiesWithLocations();

    const [getLocationDistances, { loading, data: result, error }] =
        useLazyBookingQuery<LocationDistancesQuery>(locationDistanceQuery);

    React.useEffect(() => {
        if (location?.fullAddress?.zipCode?.match(/^\d{5}$/)) {
            getLocationDistances({
                variables: {
                    zipCode: location.fullAddress.zipCode,
                },
            });
        }
    }, [getLocationDistances, location?.fullAddress?.zipCode]);

    React.useEffect(() => {
        if (loading) {
            setZipError(false);
        } else if (error) {
            setZipError(true);
        } else if (result?.nearbyLocations) {
            setOrder(result?.nearbyLocations);
        }
    }, [error, loading, setOrder, result]);

    React.useEffect(() => {
        if (zipCode?.match(/^\d{5}$/)) {
            getLocationDistances({
                variables: {
                    zipCode,
                },
            });
            setSelectedLocation('');
        } else {
            setZipError(true);
        }
    }, [getLocationDistances, zipCode]);

    React.useEffect(() => {
        if (citiesWithLocations.length > 0) {
            const defaultCity = citiesWithLocations.find(
                ({ slug }) => slug === citySlug,
            );

            if (defaultCity && !citySlug) {
                setCitySlug(defaultCity.id);
            }
        }
    }, [citySlug, citiesWithLocations]);

    const [cityLocations, setCityLocations] = React.useState<
        ReadonlyArray<LocationWithImageData>
    >([]);

    React.useEffect(() => {
        const selectedCity = citiesWithLocations.find(
            (city: CityWithLocations) => city.slug === citySlug,
        );

        if (selectedCity && citySlug !== previousCitySlug) {
            setCitySlug(selectedCity.slug);
            setCityLocations(selectedCity.locations);
        }
    }, [citiesWithLocations, citySlug, previousCitySlug]);

    const orderedLocations = React.useMemo(() => {
        if (order) {
            return order.reduce<ReadonlyArray<LocationWithImageData>>(
                (aggregator, { locationId }) => {
                    const loc = cityLocations.find(
                        ({ _id }) => locationId === _id,
                    );

                    if (loc) {
                        return [...aggregator, loc];
                    }

                    return aggregator;
                },
                [],
            );
        }

        return cityLocations;
    }, [order, cityLocations]);

    const coordinates = orderedLocations.map(
        (orderedLocation) => orderedLocation.coordinates,
    );

    // TODO: remove this once MUI issue is fixed: https://github.com/mui/material-ui/issues/43876
    // MUI issue: when there is a dropdown menu, the aria-controls are set to the dropdown, but
    // the dropdown is hidden until the user clicks on the menu, so the reference is invalid
    // until the user interacts with it. solution: we set the aria-controls to the empty string,
    // and set it correctly to their ID when the menu opens removing the attribute when it's closed.
    // there's no way to add a ref to the offending div directly so we use query selectors.
    React.useLayoutEffect(() => {
        const selectElement =
            selectRef.current?.querySelector('[role="combobox"]');

        if (
            selectElement &&
            selectElement.getAttribute('aria-controls') === ':r0:'
        ) {
            selectElement.setAttribute('aria-controls', '');
        }
    }, []);

    const handleOpen = () => {
        const selectElement =
            selectRef.current?.querySelector('[role="combobox"]');
        if (selectElement) {
            selectElement.setAttribute('aria-controls', ':r0:');
        }
    };

    const handleClose = () => {
        const selectElement =
            selectRef.current?.querySelector('[role="combobox"]');
        if (selectElement) {
            selectElement.setAttribute('aria-controls', '');
        }
    };
    // end of hacky code to fix MUI issue

    const cityOptions = React.useMemo(
        () =>
            citiesWithLocations.map(({ label, slug }: CityWithLocations) => ({
                label,
                value: slug,
            })),
        [citiesWithLocations],
    );

    const changeCity = (e: SelectChangeEvent) => {
        e.preventDefault();
        setSelectedLocation('');
        setCitySlug(e.target.value);
    };

    const changeZipCode = (e: React.ChangeEvent<{ value: string }>) => {
        e.preventDefault();
        setSelectedLocation('');
        setZipCode(e.target.value);
    };

    const selectLocation = (locationId: string) => {
        const i = orderedLocations.findIndex(({ _id }) => _id === locationId);
        scroll(i);
        setSelectedLocation(locationId);
    };

    return (
        <section className={styles.root}>
            <div className={styles.content}>
                <h2 className={styles.title}>Nearby Locations</h2>
                <div className="sr-only">
                    {intl.formatMessage({
                        id: 'location.accessibilityText',
                    })}
                </div>
                <form
                    className={classnames(styles.buttons, {
                        [styles.error]: zipError,
                    })}
                >
                    <div className={classnames(styles.label, styles.labelCity)}>
                        {intl.formatMessage({
                            id: 'location.selectCity',
                        })}
                    </div>

                    <Select
                        ref={selectRef}
                        value={citySlug}
                        onChange={changeCity}
                        onOpen={handleOpen}
                        onClose={handleClose}
                        classes={{
                            select: classnames(styles.select, styles.city),
                        }}
                        inputProps={{
                            'aria-label': 'Select a city',
                        }}
                    >
                        {cityOptions.map(({ label, value }) => (
                            <MenuItem key={value} value={value}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                    <label
                        htmlFor="zip-code"
                        className={classnames(styles.label, styles.labelZip)}
                    >
                        Enter Zipcode
                    </label>
                    <TextField
                        id="zip-code"
                        value={zipCode}
                        onChange={changeZipCode}
                        classes={{
                            root: classnames(styles.input, styles.zip),
                        }}
                        InputProps={{
                            classes: {
                                notchedOutline: styles.notchedOutline,
                                input: styles.nativeInput,
                            },
                        }}
                    />
                </form>
                {isMobile &&
                    orderedLocations &&
                    orderedLocations.length > 0 && (
                        <Swiper
                            slidesPerView="auto"
                            spaceBetween={10}
                            loop={orderedLocations.length > 1}
                            preloadImages
                            className={classnames(styles.swiper, {
                                [styles.singleSlide]:
                                    orderedLocations.length === 1,
                            })}
                        >
                            {orderedLocations?.map((nearbyLocation) => (
                                <SwiperSlide
                                    className={styles.swiperSlide}
                                    key={nearbyLocation._id}
                                >
                                    <ClinicCard
                                        location={nearbyLocation}
                                        isSelected={
                                            selectedLocation ===
                                            nearbyLocation._id
                                        }
                                        showDetailsLink={
                                            nearbyLocation._id !== location._id
                                        }
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    )}
                <div className={styles.locationsList} ref={ref}>
                    {orderedLocations.map((nearbyLocation) => (
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                        <div
                            key={nearbyLocation._id}
                            className={styles.locationButton}
                            onClick={() => selectLocation(nearbyLocation._id)}
                            onKeyDown={({ key }) => {
                                if (key === 'Enter' || key === ' ') {
                                    selectLocation(nearbyLocation._id);
                                }
                            }}
                        >
                            <ClinicCard
                                key={nearbyLocation._id}
                                location={nearbyLocation}
                                isSelected={
                                    selectedLocation === nearbyLocation._id
                                }
                                showDetailsLink={
                                    nearbyLocation._id !== location._id
                                }
                            />
                        </div>
                    ))}
                </div>
            </div>
            {!isMobile && (
                <div className={styles.mapWrapper}>
                    <div className={styles.map}>
                        <AutoSizer>
                            {(size) => {
                                return (
                                    <Map
                                        coordinates={coordinates}
                                        containerStyle={size}
                                    >
                                        {orderedLocations.map((l) => (
                                            <LocationMarker
                                                location={l}
                                                key={l._id}
                                                selected={
                                                    l._id === selectedLocation
                                                }
                                                onSelect={() =>
                                                    selectLocation(l._id)
                                                }
                                            />
                                        ))}
                                    </Map>
                                );
                            }}
                        </AutoSizer>
                    </div>
                </div>
            )}
        </section>
    );
}

export default NearbyLocations;
